import type { Questionnaire } from "fhir/r4";
import type { SetFieldType, SetNonNullable, SetRequired } from "type-fest";

import { SupportedLocale } from "@intocare/datatypes/lib/i18n";
import { CodeFromValueSetBundle, isCodeFromValueSetBundle } from "@intocare/datatypes/lib/value-set";
import { ASSESSMENT_TYPE_MODEL } from "@intocare/terminology/lib/value-set/model/assessment-type.model";

import BRADEN_FR from "./reference-data/questionnaire-BRADEN.fr-BE.json";
import BRADEN_NL from "./reference-data/questionnaire-BRADEN.nl-BE.json";
import DOLOPLUS_2_FR from "./reference-data/questionnaire-DOLOPLUS-2.fr-BE.json";
import DOLOPLUS_2_NL from "./reference-data/questionnaire-DOLOPLUS-2.nl-BE.json";
import IDDSI_FR from "./reference-data/questionnaire-IDDSI.fr-BE.json";
import IDDSI_NL from "./reference-data/questionnaire-IDDSI.nl-BE.json";
import MMSE_FR from "./reference-data/questionnaire-MMSE.fr-BE.json";
import MMSE_NL from "./reference-data/questionnaire-MMSE.nl-BE.json";
import MNA_FR from "./reference-data/questionnaire-MNA.fr-BE.json";
import MNA_NL from "./reference-data/questionnaire-MNA.nl-BE.json";
import NORTON_FR from "./reference-data/questionnaire-NORTON.fr-BE.json";
import NORTON_NL from "./reference-data/questionnaire-NORTON.nl-BE.json";
import OHAT_FR from "./reference-data/questionnaire-OHAT.fr-BE.json";
import OHAT_NL from "./reference-data/questionnaire-OHAT.nl-BE.json";
import PACSLAC_D_FR from "./reference-data/questionnaire-PACSLAC-D.fr-BE.json";
import PACSLAC_D_NL from "./reference-data/questionnaire-PACSLAC-D.nl-BE.json";
import PAINAD_FR from "./reference-data/questionnaire-PAINAD.fr-BE.json";
import PAINAD_NL from "./reference-data/questionnaire-PAINAD.nl-BE.json";
import TIMED_CHAIR_STAND_FR from "./reference-data/questionnaire-TIMED-CHAIR-STAND.fr-BE.json";
import TIMED_CHAIR_STAND_NL from "./reference-data/questionnaire-TIMED-CHAIR-STAND.nl-BE.json";
import TIMED_GET_UP_AND_GO_FR from "./reference-data/questionnaire-TIMED-GET-UP-AND-GO.fr-BE.json";
import TIMED_GET_UP_AND_GO_NL from "./reference-data/questionnaire-TIMED-GET-UP-AND-GO.nl-BE.json";
import TINETTI_FR from "./reference-data/questionnaire-TINETTI.fr-BE.json";
import TINETTI_NL from "./reference-data/questionnaire-TINETTI.nl-BE.json";
import VAS_FR from "./reference-data/questionnaire-VAS.fr-BE.json";
import VAS_NL from "./reference-data/questionnaire-VAS.nl-BE.json";

/** @deprecated Use `CodeFromValueSetBundle<typeof ASSESSMENT_TYPE_MODEL>` instead. */
export type AssessmentType = CodeFromValueSetBundle<typeof ASSESSMENT_TYPE_MODEL>;

/** @deprecated Please use the `ASSESSMENT_TYPE_MODEL` value set from `@intocare/terminology` instead. */
export const AVAILABLE_ASSESSMENT_TYPES = ASSESSMENT_TYPE_MODEL["nl-BE"].concept.map((concept) => concept.coding.code);

export const isAssessmentType = (value: string): value is AssessmentType =>
	isCodeFromValueSetBundle(ASSESSMENT_TYPE_MODEL, value);

/**
 * A FHIR {@link Questionnaire} shipped with this package, narrowed to the "profile" we guarantee: `name` is an
 * {@link AssessmentType} and `title`, `description` and `purpose` are always present. Building this from `Questionnaire`
 * via type-fest surfaces a TS error should any of these fields disappear from the base type.
 */
export type AssessmentQuestionnaire = SetFieldType<
	SetNonNullable<
		SetRequired<Questionnaire, "name" | "title" | "description" | "purpose">,
		"title" | "description" | "purpose"
	>,
	"name",
	AssessmentType
>;

const QUESTIONNAIRES_BY_TYPE: Record<AssessmentType, Record<SupportedLocale, AssessmentQuestionnaire>> = {
	BRADEN: { "fr-BE": BRADEN_FR as AssessmentQuestionnaire, "nl-BE": BRADEN_NL as AssessmentQuestionnaire },
	"DOLOPLUS-2": {
		"fr-BE": DOLOPLUS_2_FR as AssessmentQuestionnaire,
		"nl-BE": DOLOPLUS_2_NL as AssessmentQuestionnaire,
	},
	NORTON: { "fr-BE": NORTON_FR as AssessmentQuestionnaire, "nl-BE": NORTON_NL as AssessmentQuestionnaire },
	OHAT: { "fr-BE": OHAT_FR as AssessmentQuestionnaire, "nl-BE": OHAT_NL as AssessmentQuestionnaire },
	"PACSLAC-D": {
		"fr-BE": PACSLAC_D_FR as AssessmentQuestionnaire,
		"nl-BE": PACSLAC_D_NL as AssessmentQuestionnaire,
	},
	PAINAD: { "fr-BE": PAINAD_FR as AssessmentQuestionnaire, "nl-BE": PAINAD_NL as AssessmentQuestionnaire },
	TINETTI: { "fr-BE": TINETTI_FR as AssessmentQuestionnaire, "nl-BE": TINETTI_NL as AssessmentQuestionnaire },
	TIMED_CHAIR_STAND: {
		"fr-BE": TIMED_CHAIR_STAND_FR as AssessmentQuestionnaire,
		"nl-BE": TIMED_CHAIR_STAND_NL as AssessmentQuestionnaire,
	},
	TIMED_GET_UP_AND_GO: {
		"fr-BE": TIMED_GET_UP_AND_GO_FR as AssessmentQuestionnaire,
		"nl-BE": TIMED_GET_UP_AND_GO_NL as AssessmentQuestionnaire,
	},
	VAS: { "fr-BE": VAS_FR as AssessmentQuestionnaire, "nl-BE": VAS_NL as AssessmentQuestionnaire },
	IDDSI: { "fr-BE": IDDSI_FR as AssessmentQuestionnaire, "nl-BE": IDDSI_NL as AssessmentQuestionnaire },
	MMSE: { "fr-BE": MMSE_FR as AssessmentQuestionnaire, "nl-BE": MMSE_NL as AssessmentQuestionnaire },
	MNA: { "fr-BE": MNA_FR as AssessmentQuestionnaire, "nl-BE": MNA_NL as AssessmentQuestionnaire },
};

/**
 * Returns the bundled questionnaire for the given `assessmentType` in the given `locale`. Because the map is fully
 * exhaustive over `AssessmentType` × `SupportedLocale`, this function always returns a value.
 */
export const getQuestionnaireFor = (assessmentType: AssessmentType, locale: SupportedLocale): AssessmentQuestionnaire =>
	QUESTIONNAIRES_BY_TYPE[assessmentType][locale];
